import React, { useState } from 'react';
import { Skeleton } from '@design-system/Skeleton';
import { SymptomsSection } from '@screens/LevelTwoDetail/LinkedSymptoms';
import { cn } from '@/utils/styles';
import FullLengthButton from '@design-system/FullLengthButton/FullLengthButton';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { CommonlyLinkedSymptoms } from '@viome/viome-base/src/usecase/Health/Health.model';
import { EventTracker } from '@/utils';

interface AboutSectionProps {
  isLoading: boolean;
  about: string;
  title: string;
  symptoms?: CommonlyLinkedSymptoms;
  'data-testid'?: string;
}

export const AboutSection: React.FC<AboutSectionProps> = ({
  isLoading,
  about,
  symptoms,
  title,
  'data-testid': testId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { commonly_linked_symptoms: commonlyLinkedSymptoms = [] } =
    symptoms || {};

  const toggleExpand = () => {
    EventTracker.trackEvent(
      'health',
      `health_${title.replace(/\s+/g, '')}_showMore`,
    );
    setIsExpanded((prev) => !prev);
  };

  const ShowMoreIcon = isExpanded ? ChevronUp : ChevronDown;

  return (
    <div data-testid={testId}>
      <h1 className="dark typography-h1 text-primary mb-4 mt-12" data-testid="healthL2Details-aboutTitle">
        {isLoading ? <Skeleton className="h-6 w-[80%]" data-testid="healthL2Details-aboutTitleSkeleton" /> : 'About This Score'}
      </h1>

      {isLoading ? (
        <Skeleton className="h-4 w-full mb-4" data-testid="healthL2Details-aboutTextSkeleton" />
      ) : (
        <p
          className={cn(
            'dark typography-body text-secondary max-w-[37rem] mb-6',
            !isExpanded && 'line-clamp-5',
            'md:line-clamp-none',
          )}
          data-testid="healthL2Details-aboutText"
        >
          {about}
        </p>
      )}

      {isLoading ? (
        <Skeleton className="h-4 w-full mb-4" data-testid="healthL2Details-symptomsSkeleton" />
      ) : (
        <div className={cn(isExpanded ? 'block' : 'hidden', 'md:block')} data-testid="healthL2Details-symptomsContainer">
          <SymptomsSection symptoms={commonlyLinkedSymptoms} data-testid="healthL2Details-symptoms" />
        </div>
      )}

      {/* Show button only on small screens */}
      <div className="block md:hidden" data-testid="healthL2Details-expandButtonContainer">
        {!isLoading && (
          <FullLengthButton
            textStyle="typography-btn-large"
            buttonStyle="bg-[#42434D73] flex items-center justify-center mt-6 px-4 py-2 cursor-pointer h-12 rounded-full w-full"
            text={isExpanded ? 'Show Less' : 'Show More'}
            rightIconSrc={<ShowMoreIcon className="h-6 w-4 ml-1.5" />}
            onClick={toggleExpand}
          />
        )}
      </div>
    </div>
  );
};
